// Theming dialog flags and submit handlers. Cross-cutting concerns
// (invalidateSurveyCaches, setModQShown) are injected from build.js.
// Mutators are stored in an internal ref to break the render-order cycle:
// build.js calls useThemingState before useThemingData, so mutators
// passed in may be one render behind. The ref updates every render and
// handlers read lazily.

import { useRef, useState } from "react";
import { toast } from "react-toastify";

import { fetcherFull } from "utils/api";
import { deleteTheme } from "../utils/delete-theme";

export function useThemingState({
    surveyId,
    themesDataMutate,
    answerThemesMutate,
    themeAnswersMutate,
    invalidateSurveyCaches,
    setModQShown,
}) {
    const [themeEdit, setThemeEdit] = useState(undefined);
    const [answerTheming, setAnswerTheming] = useState(undefined);
    const [showThemeDialog, setShowThemeDialog] = useState(false);
    const [showThemeDeleteDialog, setShowThemeDeleteDialog] = useState(false);
    const [showThemeAnswersDialog, setShowThemeAnswersDialog] = useState(false);
    const [showAnswerThemingDialog, setShowAnswerThemingDialog] = useState(
        false,
    );

    const mutatorsRef = useRef({});
    mutatorsRef.current.themesDataMutate = themesDataMutate;
    mutatorsRef.current.answerThemesMutate = answerThemesMutate;
    mutatorsRef.current.themeAnswersMutate = themeAnswersMutate;

    const handleThemeEditSubmit = async (values) => {
        const isEdit = Boolean(themeEdit?.id);
        const themeObj = {
            name: values.name,
            description: values.description,
        };
        let response;
        try {
            response = isEdit
                ? await fetcherFull(
                    `/v2/survey/${surveyId}/theme/${themeEdit.id}`,
                    "PUT",
                    themeObj,
                )
                : await fetcherFull(
                    `/v2/survey/${surveyId}/theme`,
                    "POST",
                    themeObj,
                );
        } catch (err) {
            toast.error(
                `Error ${isEdit ? "editing" : "creating"} Theme, ${
                    err?.message || "Unknown error"
                }`,
            );
            console.error(err);
            return;
        }
        const ok = isEdit
            ? Boolean(response?.id)
            : Boolean(response?.name);
        if (ok) {
            mutatorsRef.current.themesDataMutate?.();
            invalidateSurveyCaches();
            setThemeEdit(undefined);
            setShowThemeDialog(false);
            toast.success(isEdit ? "Theme Edited" : "Theme Created");
        } else {
            const serverError =
                response?.error ||
                `Unknown issue ${isEdit ? "editing" : "creating"} Theme.`;
            toast.error(
                `Error ${isEdit ? "editing" : "creating"} Theme, ${serverError}`,
            );
            console.error(response?.errors);
        }
    };

    const handleDeleteThemeSubmit = async (themeId) => {
        setShowThemeDeleteDialog(false);
        const ok = await deleteTheme({
            surveyId,
            themeId,
            themesDataMutate: mutatorsRef.current.themesDataMutate,
            invalidateSurveyCaches,
        });
        if (ok) {
            setThemeEdit(undefined);
        }
    };

    const handleAnswerThemingSubmit = async (values) => {
        let response;
        try {
            response = await fetcherFull(
                `/v2/response/answer/${answerTheming}/themes`,
                "POST",
                values,
            );
        } catch (err) {
            toast.error(`Error theming answer, ${err?.message || "Unknown error"}`);
            console.error(err);
            return;
        }
        if (response) {
            if (response?.themes) {
                mutatorsRef.current.themesDataMutate?.();
                mutatorsRef.current.answerThemesMutate?.();
                mutatorsRef.current.themeAnswersMutate?.();
                invalidateSurveyCaches();
            }
        }
    };

    const handleThemeAnswersSubmit = async (values) => {
        if (!themeEdit) {
            return;
        }
        const themeObj = {
            preset: values.answers_preset,
            custom: values.answers_custom,
        };
        let response;
        try {
            response = await fetcherFull(
                `/v2/survey/${surveyId}/theme/${themeEdit.id}`,
                "POST",
                themeObj,
            );
        } catch (err) {
            toast.error(`Error editing Theme, ${err?.message || "Unknown error"}`);
            console.error(err);
            return;
        }
        if (response?.id) {
            mutatorsRef.current.themesDataMutate?.();
            mutatorsRef.current.answerThemesMutate?.();
            mutatorsRef.current.themeAnswersMutate?.();
            invalidateSurveyCaches();
            setModQShown(undefined);
            setThemeEdit(undefined);
            toast.success("Theme Edited");
        } else {
            const serverError = response?.error || "Unknown issue editing Theme.";
            toast.error(`Error editing Theme, ${serverError}`);
            console.error(response?.errors);
        }
    };

    return {
        themeEdit,
        setThemeEdit,
        answerTheming,
        setAnswerTheming,
        showThemeDialog,
        setShowThemeDialog,
        showThemeDeleteDialog,
        setShowThemeDeleteDialog,
        showThemeAnswersDialog,
        setShowThemeAnswersDialog,
        showAnswerThemingDialog,
        setShowAnswerThemingDialog,
        handleThemeEditSubmit,
        handleDeleteThemeSubmit,
        handleAnswerThemingSubmit,
        handleThemeAnswersSubmit,
    };
}
